import React from "react";

import { Icon, IconSize } from "@gaming/xds-react-js";
import { Launch, Warning } from "@gaming/xds-react-js-icons";

import { microsoftRoyaltiesUrl, microsoftSupplierWebUrl } from "../../constants";
import { useStrings } from "../../localization/localizationHelpers";
import { getRoyaltySetupStatus, RoyaltySetupStatus } from "../../models/partnerOverview";
import * as styles from "./RoyaltySetupCard.module.css";

type RoyaltySetupCardProps = Readonly<{
  status?: RoyaltySetupStatus;
  vendorId?: string | null;
  primaryContactEmail?: string;
}>;

export const RoyaltySetupCard = ({
  status: statusOverride,
  vendorId,
  primaryContactEmail,
}: RoyaltySetupCardProps) => {
  const [
    setupTitle,
    completedTitle,
    notStartedLabel,
    inProgressLabel,
    completedLabel,
    supplierIntro,
    supplierWebLabel,
    invitationRequired,
    invitationGuidance,
    idSetupLabel,
    invitationGuidanceSuffix,
    invitationSent,
    importantLabel,
    currencyWarning,
    completeSetupAction,
    supplierProfileDescription,
    royaltiesAction,
    royaltiesDescription,
    maintenanceWarning,
    vendorIdLabel,
  ] = useStrings(
    "royaltySetupTitle",
    "royaltySetupCompletedTitle",
    "royaltySetupNotStartedStatus",
    "royaltySetupInProgressStatus",
    "royaltySetupCompletedStatus",
    "royaltySetupSupplierIntro",
    "royaltySetupSupplierWebLabel",
    "royaltySetupInvitationRequired",
    "royaltySetupInvitationGuidance",
    "royaltySetupIdSetupLabel",
    "royaltySetupInvitationGuidanceSuffix",
    "royaltySetupInvitationSent",
    "royaltySetupImportantLabel",
    "royaltySetupCurrencyWarning",
    "royaltySetupContinueAction",
    "royaltySetupSupplierProfileDescription",
    "royaltySetupRoyaltiesAction",
    "royaltySetupRoyaltiesDescription",
    "royaltySetupMaintenanceWarning",
    "royaltySetupVendorIdLabel"
  );
  const status = statusOverride ?? getRoyaltySetupStatus(vendorId);
  const isCompleted = status === "completed";
  const isInProgress = status === "inProgress";
  const statusLabels: Record<RoyaltySetupStatus, string> = {
    notStarted: notStartedLabel,
    inProgress: inProgressLabel,
    completed: completedLabel,
  };

  return (
    <section className={styles.section}>
      <div className={styles.card}>
        <div className={styles.header}>
          <div>
            <h2 className={styles.title}>{isCompleted ? completedTitle : setupTitle}</h2>
            {isCompleted && (
              <p className={styles.vendorId}>
                <strong>{vendorIdLabel}</strong>
                <span>{vendorId?.trim()}</span>
              </p>
            )}
          </div>
          <span className={`${styles.status} ${styles[status]}`}>
            <span className={styles.statusDot} aria-hidden="true" />
            {statusLabels[status]}
          </span>
        </div>
        {isCompleted ? (
          <>
            <div className={styles.completedLinks}>
              <a
                className={styles.action}
                href={microsoftSupplierWebUrl}
                target="_blank"
                rel="noopener noreferrer"
              >
                <span>{supplierProfileDescription}</span>
                {supplierWebLabel}
                <Icon aria-hidden="true" icon={Launch} iconSize={IconSize.XSmall} />
              </a>
              <a
                className={styles.action}
                href={microsoftRoyaltiesUrl}
                target="_blank"
                rel="noopener noreferrer"
              >
                <span>{royaltiesDescription}</span>
                {royaltiesAction}
                <Icon aria-hidden="true" icon={Launch} iconSize={IconSize.XSmall} />
              </a>
            </div>
            <div className={`${styles.notice} ${styles.warning}`}>
              <Icon aria-hidden="true" icon={Warning} iconSize={IconSize.XSmall} />
              <p>
                <strong>{importantLabel}</strong> {maintenanceWarning}
              </p>
            </div>
          </>
        ) : (
          <>
            <p className={styles.description}>
              {supplierIntro}
              {isInProgress &&
                primaryContactEmail &&
                ` ${invitationSent.replace("{0}", primaryContactEmail)}`}
            </p>
            {!isInProgress && (
              <>
                <p className={styles.description}>{invitationRequired}</p>
                <p className={styles.description}>
                  {invitationGuidance}{" "}
                  <a href="mailto:ID@XBOX" title="mailto:id@xbox" aria-label={idSetupLabel}>
                    {idSetupLabel}
                  </a>
                  {invitationGuidanceSuffix}
                </p>
              </>
            )}
            {isInProgress && (
              <>
                <div className={`${styles.notice} ${styles.warning}`}>
                  <Icon aria-hidden="true" icon={Warning} iconSize={IconSize.XSmall} />
                  <p>
                    <strong>{importantLabel}</strong> {currencyWarning}
                  </p>
                </div>
                <p className={styles.description}>
                  {invitationGuidance}{" "}
                  <a href="mailto:ID@XBOX" title="mailto:id@xbox" aria-label={idSetupLabel}>
                    {idSetupLabel}
                  </a>
                  {invitationGuidanceSuffix}
                </p>
                <a
                  className={styles.action}
                  href={microsoftSupplierWebUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {completeSetupAction}
                  <Icon aria-hidden="true" icon={Launch} iconSize={IconSize.XSmall} />
                </a>
              </>
            )}
          </>
        )}
      </div>
    </section>
  );
};
